import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from '../components/shared/Modal';
import Toast from '../../../shared/components/ui/Toast';
import {
  billingOrgSettingsService,
  billingInvoicesService,
  billingPlansService,
  billingPricingRulesService,
  organizationsService,
  BILLING_CONDITION_FIELD_KEYS,
  type BillingInvoice,
  type BillingInvoiceLineItem,
  type BillingInvoiceRun,
  type BillingPlanEntitlement,
  type BillingOrgSettings,
  type BillingPlan,
  type BillingPricingRuleCondition,
  type BillingPricingRule,
  type RuleOperator,
  type Organization,
} from '../services';

interface BillingPageProps {
  orgId: string;
}

type BillingSection = 'plans' | 'pricing-rules' | 'invoices' | 'runs' | 'billing-settings';

interface InvoicePreviewLineItem {
  line_type: 'subscription' | 'usage' | 'adjustment' | 'tax';
  metric: string | null;
  description: string;
  quantity: number | null;
  unit_price: number | null;
  amount: number;
  metadata: Record<string, unknown> | null;
}

interface InvoicePreview {
  currency: string;
  subtotal_amount: number;
  tax_amount: number;
  tax_rate_percent: number;
  prices_tax_inclusive: boolean;
  tax_label: string;
  total_amount: number;
  line_items: InvoicePreviewLineItem[];
  note: string;
}

const BillingPage: React.FC<BillingPageProps> = ({ orgId }) => {
  const location = useLocation();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [rules, setRules] = useState<BillingPricingRule[]>([]);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [runs, setRuns] = useState<BillingInvoiceRun[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [selectedRuleId, setSelectedRuleId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRunCreating, setIsRunCreating] = useState(false);
  const [isRunExecuting, setIsRunExecuting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddPlanModalOpen, setIsAddPlanModalOpen] = useState(false);
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] = useState(false);
  const [isInvoicePreviewLoading, setIsInvoicePreviewLoading] = useState(false);
  const [isEditPlanModalOpen, setIsEditPlanModalOpen] = useState(false);
  const [isEditRuleModalOpen, setIsEditRuleModalOpen] = useState(false);
  const [isEditEntitlementsModalOpen, setIsEditEntitlementsModalOpen] = useState(false);
  const [isEditConditionsModalOpen, setIsEditConditionsModalOpen] = useState(false);
  const [editEntitlementsView, setEditEntitlementsView] = useState<'list' | 'form'>('list');
  const [editConditionsView, setEditConditionsView] = useState<'list' | 'form'>('list');
  const [isViewingDetailsLoading, setIsViewingDetailsLoading] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<BillingPlan | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<BillingPricingRule | null>(null);
  const [entitlementToDelete, setEntitlementToDelete] = useState<BillingPlanEntitlement | null>(null);
  const [conditionToDelete, setConditionToDelete] = useState<BillingPricingRuleCondition | null>(null);
  const [editingConditionId, setEditingConditionId] = useState<string | null>(null);
  const [addPlanEntitlementDrafts, setAddPlanEntitlementDrafts] = useState<
    Array<{
      metric: 'video_minutes' | 'storage';
      includedUnits: string;
      carryoverEnabled: boolean;
      carryoverCapType: 'fixed' | 'percent_of_included';
      carryoverCapValue: string;
      carryoverExpiryCycles: string;
      carryoverConsumptionOrder: 'carryover_first' | 'current_cycle_first';
    }>
  >([]);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [editingEntitlementId, setEditingEntitlementId] = useState<string | null>(null);
  const [viewingPlanName, setViewingPlanName] = useState<string>('');
  const [viewingRuleName, setViewingRuleName] = useState<string>('');
  const [viewingEntitlements, setViewingEntitlements] = useState<BillingPlanEntitlement[]>([]);
  const [viewingConditions, setViewingConditions] = useState<BillingPricingRuleCondition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [toast, setToast] = useState<{ isVisible: boolean; type: 'success' | 'error' | 'info' | 'warning'; message: string }>({
    isVisible: false,
    type: 'info',
    message: '',
  });
  const [invoicePreview, setInvoicePreview] = useState<InvoicePreview | null>(null);
  const [billingSettingsList, setBillingSettingsList] = useState<BillingOrgSettings[]>([]);
  const [isSavingBillingDefaults, setIsSavingBillingDefaults] = useState(false);
  const [isBillingSettingsModalOpen, setIsBillingSettingsModalOpen] = useState(false);
  const [editingBillingSettingOrgId, setEditingBillingSettingOrgId] = useState<string | null>(null);
  const [viewInvoice, setViewInvoice] = useState<BillingInvoice | null>(null);
  const [viewInvoiceLineItems, setViewInvoiceLineItems] = useState<BillingInvoiceLineItem[]>([]);
  const [editingInvoice, setEditingInvoice] = useState<BillingInvoice | null>(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState<BillingInvoice | null>(null);

  const [planForm, setPlanForm] = useState({
    organizationScope: 'current' as 'current' | 'global' | 'specific',
    specificOrganizationId: '',
    name: '',
    cadence: 'monthly' as 'monthly' | 'quarterly' | 'yearly' | 'custom',
    baseFee: '0',
    currency: 'INR',
  });

  const [entitlementForm, setEntitlementForm] = useState({
    metric: 'video_minutes' as 'video_minutes' | 'storage',
    includedUnits: '0',
    carryoverEnabled: false,
    carryoverCapType: 'fixed' as 'fixed' | 'percent_of_included',
    carryoverCapValue: '0',
    carryoverExpiryCycles: '1',
    carryoverConsumptionOrder: 'carryover_first' as 'carryover_first' | 'current_cycle_first',
  });

  const [ruleForm, setRuleForm] = useState({
    organizationScope: 'current' as 'current' | 'global' | 'specific',
    specificOrganizationId: '',
    name: '',
    metric: 'video_minutes' as 'video_minutes' | 'storage' | 'service_combo',
    priority: '100',
    rateType: 'per_minute' as 'per_minute' | 'per_gb' | 'flat' | 'slab_flat' | 'slab_progressive',
    rateValue: '0',
    comboMode: 'contains' as 'exact' | 'contains',
    comboRateMode: 'additive' as 'override' | 'additive',
    comboServicesCsv: '',
  });

  const [conditionForm, setConditionForm] = useState({
    fieldKey: '',
    operator: '>=' as RuleOperator,
    fieldValue: '',
  });

  const [invoiceFilters, setInvoiceFilters] = useState({
    organisationId: '', // default: All organizations
    status: '',
    fromDate: '',
    toDate: '',
  });

  const [invoiceForm, setInvoiceForm] = useState({
    organisationId: orgId || '',
    invoiceType: 'usage_settlement_end' as BillingInvoice['invoice_type'],
    periodStart: '',
    periodEnd: '',
    billingPlanId: '',
    currency: 'INR',
    taxRatePercent: '0',
    pricesTaxInclusive: false,
    taxLabel: 'Tax',
    status: 'draft' as BillingInvoice['status'],
    dueAt: '',
  });
  const [invoiceOrgSearchQuery, setInvoiceOrgSearchQuery] = useState('');
  const [invoiceOrgDropdownOpen, setInvoiceOrgDropdownOpen] = useState(false);

  const [billingDefaultsForm, setBillingDefaultsForm] = useState({
    organisationId: orgId || '',
    currency: 'INR',
    taxRatePercent: '0',
    pricesTaxInclusive: false,
    taxLabel: 'Tax',
  });

  const currentSection: BillingSection = useMemo(() => {
    if (location.pathname.includes('/control-room/pricing-rules')) return 'pricing-rules';
    if (location.pathname.includes('/control-room/invoices')) return 'invoices';
    if (location.pathname.includes('/control-room/runs')) return 'runs';
    if (location.pathname.includes('/control-room/billing-settings')) return 'billing-settings';
    return 'plans';
  }, [location.pathname]);

  const sectionHeader = useMemo(() => {
    if (currentSection === 'plans') {
      return {
        title: 'Plans',
        description: 'Manage billing plans and entitlements.',
      };
    }
    if (currentSection === 'pricing-rules') {
      return {
        title: 'Pricing Rules',
        description: 'Configure pricing rules and condition logic.',
      };
    }
    if (currentSection === 'invoices') {
      return {
        title: 'Invoices',
        description: 'View, filter, preview, and create invoices.',
      };
    }
    if (currentSection === 'runs') {
      return {
        title: 'Runs',
        description: 'Track billing run execution and retry workflow.',
      };
    }
    return {
      title: 'Billing Settings',
      description: 'Configure tax and currency defaults for invoice creation.',
    };
  }, [currentSection]);

  const orgNameById = useMemo(() => {
    return organizations.reduce<Record<string, string>>((acc, item) => {
      acc[item.id] = item.name;
      return acc;
    }, {});
  }, [organizations]);

  const resolveScopeOrgId = (scope: 'current' | 'global' | 'specific', specificId: string): string | null => {
    if (scope === 'global') return null;
    if (scope === 'specific') return specificId || null;
    return orgId || null;
  };

  const getCurrentMonthDateRange = (): { start: string; end: string } => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  };

  const loadOrgBillingDefaults = useCallback(
    async (targetOrgId: string) => {
      if (!targetOrgId) return null;
      try {
        const settings = await billingOrgSettingsService.getByOrganisationId(targetOrgId);
        return settings;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load billing defaults.');
        return null;
      }
    },
    []
  );

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const invoiceParams = {
        organisationId: invoiceFilters.organisationId || undefined,
        status: (invoiceFilters.status || undefined) as BillingInvoice['status'] | undefined,
        fromDate: invoiceFilters.fromDate ? `${invoiceFilters.fromDate}T00:00:00.000Z` : undefined,
        toDate: invoiceFilters.toDate ? `${invoiceFilters.toDate}T23:59:59.999Z` : undefined,
      };
      const [orgsData, plansData, rulesData, invoicesData, runsData, billingSettingsData] = await Promise.all([
        organizationsService.getAll(),
        billingPlansService.getAll(),
        billingPricingRulesService.getAll(),
        billingInvoicesService.getInvoices(invoiceParams),
        billingInvoicesService.getRuns(25),
        billingOrgSettingsService.getAll(),
      ]);

      setOrganizations(orgsData);
      setPlans(plansData);
      setRules(rulesData);
      setInvoices(invoicesData);
      setRuns(runsData);
      setBillingSettingsList(billingSettingsData);

      setSelectedPlanId((prev) => prev || plansData[0]?.id || '');
      setSelectedRuleId((prev) => prev || rulesData[0]?.id || '');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load billing data.');
    } finally {
      setIsLoading(false);
    }
  }, [invoiceFilters.fromDate, invoiceFilters.organisationId, invoiceFilters.status, invoiceFilters.toDate]);

  useEffect(() => {
    const { start, end } = getCurrentMonthDateRange();
    setInvoiceForm((prev) => ({
      ...prev,
      organisationId: prev.organisationId || orgId || '',
      periodStart: prev.periodStart || start,
      periodEnd: prev.periodEnd || end,
      currency: prev.currency || 'INR',
      taxRatePercent: prev.taxRatePercent || '0',
      taxLabel: prev.taxLabel || 'Tax',
    }));
    setInvoiceFilters((prev) => ({ ...prev })); // keep organisationId as-is (default '' = All organizations)
    setBillingDefaultsForm((prev) => ({ ...prev, organisationId: prev.organisationId || orgId || '' }));
  }, [orgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!error) return;
    setToast({
      isVisible: true,
      type: 'error',
      message: error,
    });
    setError(null);
  }, [error]);

  useEffect(() => {
    if (!successMessage) return;
    setToast({
      isVisible: true,
      type: 'success',
      message: successMessage,
    });
    setSuccessMessage(null);
  }, [successMessage]);

  const handleCreatePlan = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!planForm.name.trim()) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const scopedOrgId = resolveScopeOrgId(planForm.organizationScope, planForm.specificOrganizationId);
      const plan = await billingPlansService.create({
        organisation_id: scopedOrgId,
        name: planForm.name.trim(),
        cadence: planForm.cadence,
        currency: planForm.currency.trim() || 'INR',
        base_subscription_fee: Number(planForm.baseFee || '0'),
      });
      for (const draft of addPlanEntitlementDrafts) {
        const isCarryoverAllowed = draft.metric === 'video_minutes';
        const carryoverEnabled = isCarryoverAllowed ? draft.carryoverEnabled : false;
        await billingPlansService.createEntitlement({
          billing_plan_id: plan.id,
          metric: draft.metric,
          included_units: Number(draft.includedUnits || '0'),
          carryover_enabled: carryoverEnabled,
          carryover_cap_type: carryoverEnabled ? draft.carryoverCapType : null,
          carryover_cap_value: carryoverEnabled ? Number(draft.carryoverCapValue || '0') : null,
          carryover_expiry_cycles: Math.min(1, Number(draft.carryoverExpiryCycles || '1')),
          carryover_consumption_order: draft.carryoverConsumptionOrder,
        });
      }
      setPlanForm((prev) => ({ ...prev, name: '', baseFee: '0', specificOrganizationId: '' }));
      setAddPlanEntitlementDrafts([]);
      setIsAddPlanModalOpen(false);
      setSuccessMessage(
        addPlanEntitlementDrafts.length > 0
          ? `Billing plan created with ${addPlanEntitlementDrafts.length} entitlement(s).`
          : 'Billing plan created.'
      );
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create billing plan.');
    } finally {
      setIsSaving(false);
    }
  };

  const addPlanAddEntitlementDraft = () => {
    setAddPlanEntitlementDrafts((prev) => [
      ...prev,
      {
        metric: 'video_minutes' as const,
        includedUnits: '0',
        carryoverEnabled: false,
        carryoverCapType: 'fixed' as const,
        carryoverCapValue: '0',
        carryoverExpiryCycles: '1',
        carryoverConsumptionOrder: 'carryover_first' as const,
      },
    ]);
  };

  const addPlanRemoveEntitlementDraft = (index: number) => {
    setAddPlanEntitlementDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const addPlanUpdateEntitlementDraft = (
    index: number,
    patch: Partial<(typeof addPlanEntitlementDrafts)[0]>
  ) => {
    setAddPlanEntitlementDrafts((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d))
    );
  };

  const handleOpenEditPlan = (plan: BillingPlan) => {
    setSelectedPlanId(plan.id);
    setEditingPlanId(plan.id);
    setPlanForm({
      organizationScope: plan.organisation_id ? (plan.organisation_id === orgId ? 'current' : 'specific') : 'global',
      specificOrganizationId: plan.organisation_id || '',
      name: plan.name || '',
      cadence: plan.cadence,
      baseFee: String(plan.base_subscription_fee ?? 0),
      currency: plan.currency || 'INR',
    });
    setIsEditPlanModalOpen(true);
  };

  const handleUpdatePlan = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingPlanId) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const scopedOrgId = resolveScopeOrgId(planForm.organizationScope, planForm.specificOrganizationId);
      await billingPlansService.update(editingPlanId, {
        organisation_id: scopedOrgId,
        name: planForm.name.trim(),
        cadence: planForm.cadence,
        currency: planForm.currency.trim() || 'INR',
        base_subscription_fee: Number(planForm.baseFee || '0'),
      });
      setIsEditPlanModalOpen(false);
      setEditingPlanId(null);
      setSuccessMessage('Billing plan updated.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update billing plan.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEntitlement = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedPlanId) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const isCarryoverAllowed = entitlementForm.metric === 'video_minutes';
      const carryoverEnabled = isCarryoverAllowed ? entitlementForm.carryoverEnabled : false;
      if (editingEntitlementId) {
        await billingPlansService.updateEntitlement(editingEntitlementId, {
          included_units: Number(entitlementForm.includedUnits || '0'),
          carryover_enabled: carryoverEnabled,
          carryover_cap_type: carryoverEnabled ? entitlementForm.carryoverCapType : null,
          carryover_cap_value: carryoverEnabled ? Number(entitlementForm.carryoverCapValue || '0') : null,
          carryover_expiry_cycles: Math.min(1, Number(entitlementForm.carryoverExpiryCycles || '1')),
          carryover_consumption_order: entitlementForm.carryoverConsumptionOrder,
        });
        setSuccessMessage('Entitlement updated.');
      } else {
        await billingPlansService.createEntitlement({
          billing_plan_id: selectedPlanId,
          metric: entitlementForm.metric,
          included_units: Number(entitlementForm.includedUnits || '0'),
          carryover_enabled: carryoverEnabled,
          carryover_cap_type: carryoverEnabled ? entitlementForm.carryoverCapType : null,
          carryover_cap_value: carryoverEnabled ? Number(entitlementForm.carryoverCapValue || '0') : null,
          carryover_expiry_cycles: Math.min(1, Number(entitlementForm.carryoverExpiryCycles || '1')),
          carryover_consumption_order: entitlementForm.carryoverConsumptionOrder,
        });
        setSuccessMessage('Entitlement added.');
      }

      setEditingEntitlementId(null);
      if (isEditEntitlementsModalOpen) {
        setEditEntitlementsView('list');
        const rows = await billingPlansService.getEntitlementsByPlanId(selectedPlanId);
        setViewingEntitlements(rows);
      } else {
        await loadData();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save entitlement.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenAddEntitlementInModal = () => {
    setEditingEntitlementId(null);
    setEntitlementForm({
      metric: 'video_minutes',
      includedUnits: '0',
      carryoverEnabled: false,
      carryoverCapType: 'fixed',
      carryoverCapValue: '0',
      carryoverExpiryCycles: '1',
      carryoverConsumptionOrder: 'carryover_first',
    });
    setEditEntitlementsView('form');
  };

  const handleOpenEditEntitlementInModal = (entitlement: BillingPlanEntitlement) => {
    setEditingEntitlementId(entitlement.id);
    setEntitlementForm({
      metric: entitlement.metric,
      includedUnits: String(entitlement.included_units ?? 0),
      carryoverEnabled: Boolean(entitlement.carryover_enabled),
      carryoverCapType: (entitlement.carryover_cap_type || 'fixed') as 'fixed' | 'percent_of_included',
      carryoverCapValue: String(entitlement.carryover_cap_value ?? 0),
      carryoverExpiryCycles: String(Math.min(1, entitlement.carryover_expiry_cycles ?? 1)),
      carryoverConsumptionOrder: entitlement.carryover_consumption_order || 'carryover_first',
    });
    setEditEntitlementsView('form');
  };

  const handleOpenEditEntitlements = async (plan: BillingPlan) => {
    setSelectedPlanId(plan.id);
    setViewingPlanName(plan.name);
    setEditEntitlementsView('list');
    setEditingEntitlementId(null);
    setIsEditEntitlementsModalOpen(true);
    setIsViewingDetailsLoading(true);
    setError(null);
    try {
      const rows = await billingPlansService.getEntitlementsByPlanId(plan.id);
      setViewingEntitlements(rows);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load entitlements.');
      setViewingEntitlements([]);
    } finally {
      setIsViewingDetailsLoading(false);
    }
  };

  const handleCloseEditEntitlements = () => {
    setIsEditEntitlementsModalOpen(false);
    setEditEntitlementsView('list');
    setViewingPlanName('');
    setViewingEntitlements([]);
    setEditingEntitlementId(null);
  };

  const handleConfirmDeleteEntitlement = async () => {
    if (!entitlementToDelete) return;
    setError(null);
    try {
      await billingPlansService.deleteEntitlement(entitlementToDelete.id);
      setSuccessMessage('Entitlement removed.');
      const rows = await billingPlansService.getEntitlementsByPlanId(selectedPlanId);
      setViewingEntitlements(rows);
      setEntitlementToDelete(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete entitlement.');
    }
  };

  const handleOpenEditCondition = (condition: BillingPricingRuleCondition) => {
    setEditingConditionId(condition.id);
    setConditionForm({
      fieldKey: condition.field_key,
      operator: condition.operator,
      fieldValue: condition.field_value,
    });
    setEditConditionsView('form');
  };

  const handleOpenEditConditions = async (rule: BillingPricingRule) => {
    setSelectedRuleId(rule.id);
    setViewingRuleName(rule.name);
    setEditConditionsView('list');
    setEditingConditionId(null);
    setIsEditConditionsModalOpen(true);
    setIsViewingDetailsLoading(true);
    setError(null);
    try {
      const rows = await billingPricingRulesService.getConditions(rule.id);
      setViewingConditions(rows);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load conditions.');
      setViewingConditions([]);
    } finally {
      setIsViewingDetailsLoading(false);
    }
  };

  const handleCloseEditConditions = () => {
    setIsEditConditionsModalOpen(false);
    setEditConditionsView('list');
    setViewingRuleName('');
    setViewingConditions([]);
    setEditingConditionId(null);
  };

  const handleUpdateCondition = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingConditionId || !conditionForm.fieldKey.trim()) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await billingPricingRulesService.updateCondition(editingConditionId, {
        field_key: conditionForm.fieldKey.trim(),
        operator: conditionForm.operator,
        field_value: conditionForm.fieldValue.trim(),
      });
      setEditConditionsView('list');
      setEditingConditionId(null);
      setConditionForm({ fieldKey: '', operator: '>=', fieldValue: '' });
      const rows = await billingPricingRulesService.getConditions(selectedRuleId);
      setViewingConditions(rows);
      setSuccessMessage('Condition updated.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update condition.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDeleteCondition = async () => {
    if (!conditionToDelete) return;
    setError(null);
    try {
      await billingPricingRulesService.deleteCondition(conditionToDelete.id);
      setSuccessMessage('Condition removed.');
      const rows = await billingPricingRulesService.getConditions(selectedRuleId);
      setViewingConditions(rows);
      setConditionToDelete(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete condition.');
    }
  };

  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await billingPlansService.deletePlan(planToDelete.id);
      setPlanToDelete(null);
      setSuccessMessage('Billing plan deleted.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete plan.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await billingPricingRulesService.deleteRule(ruleToDelete.id);
      setRuleToDelete(null);
      setSuccessMessage('Pricing rule deleted.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete rule.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateRule = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ruleForm.name.trim()) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const scopedOrgId = resolveScopeOrgId(ruleForm.organizationScope, ruleForm.specificOrganizationId);
      const services = ruleForm.comboServicesCsv
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      await billingPricingRulesService.create({
        organisation_id: scopedOrgId,
        metric: ruleForm.metric,
        name: ruleForm.name.trim(),
        priority: Number(ruleForm.priority || '100'),
        rate_type: ruleForm.rateType,
        rate_value: Number(ruleForm.rateValue || '0'),
        service_combo_match_mode: ruleForm.metric === 'service_combo' ? ruleForm.comboMode : null,
        service_combo_rate_mode: ruleForm.metric === 'service_combo' ? ruleForm.comboRateMode : null,
        service_combo_services: ruleForm.metric === 'service_combo' ? services : null,
      });

      setRuleForm((prev) => ({ ...prev, name: '', rateValue: '0', comboServicesCsv: '', specificOrganizationId: '' }));
      setIsAddRuleModalOpen(false);
      setSuccessMessage('Pricing rule created.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create pricing rule.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenEditRule = (rule: BillingPricingRule) => {
    setSelectedRuleId(rule.id);
    setEditingRuleId(rule.id);
    setRuleForm({
      organizationScope: rule.organisation_id ? (rule.organisation_id === orgId ? 'current' : 'specific') : 'global',
      specificOrganizationId: rule.organisation_id || '',
      name: rule.name || '',
      metric: rule.metric,
      priority: String(rule.priority ?? 100),
      rateType: rule.rate_type,
      rateValue: String(rule.rate_value ?? 0),
      comboMode: (rule.service_combo_match_mode || 'contains') as 'exact' | 'contains',
      comboRateMode: (rule.service_combo_rate_mode || 'additive') as 'override' | 'additive',
      comboServicesCsv: (rule.service_combo_services || []).join(', '),
    });
    setIsEditRuleModalOpen(true);
  };

  const handleUpdateRule = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingRuleId) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const scopedOrgId = resolveScopeOrgId(ruleForm.organizationScope, ruleForm.specificOrganizationId);
      const services = ruleForm.comboServicesCsv
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      await billingPricingRulesService.update(editingRuleId, {
        organisation_id: scopedOrgId,
        metric: ruleForm.metric,
        name: ruleForm.name.trim(),
        priority: Number(ruleForm.priority || '100'),
        rate_type: ruleForm.rateType,
        rate_value: Number(ruleForm.rateValue || '0'),
        service_combo_match_mode: ruleForm.metric === 'service_combo' ? ruleForm.comboMode : null,
        service_combo_rate_mode: ruleForm.metric === 'service_combo' ? ruleForm.comboRateMode : null,
        service_combo_services: ruleForm.metric === 'service_combo' ? services : null,
      });
      setIsEditRuleModalOpen(false);
      setEditingRuleId(null);
      setSuccessMessage('Pricing rule updated.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update pricing rule.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateCondition = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedRuleId || !conditionForm.fieldKey.trim()) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await billingPricingRulesService.createCondition({
        billing_pricing_rule_id: selectedRuleId,
        field_key: conditionForm.fieldKey.trim(),
        operator: conditionForm.operator,
        field_value: conditionForm.fieldValue.trim(),
      });
      setConditionForm({ fieldKey: '', operator: '>=', fieldValue: '' });
      if (isEditConditionsModalOpen) {
        setEditConditionsView('list');
        const rows = await billingPricingRulesService.getConditions(selectedRuleId);
        setViewingConditions(rows);
      } else {
        await loadData();
      }
      setSuccessMessage('Rule condition added.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create rule condition.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenAddConditionInModal = () => {
    setEditingConditionId(null);
    setConditionForm({
      fieldKey: BILLING_CONDITION_FIELD_KEYS[0].value,
      operator: '>=',
      fieldValue: '',
    });
    setEditConditionsView('form');
  };

  const handleCreateDryRun = async () => {
    setIsRunCreating(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const now = new Date();
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0));
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59));
      await billingInvoicesService.createRun({
        organisation_id: orgId,
        period_start: start.toISOString(),
        period_end: end.toISOString(),
        dry_run: true,
      });
      setSuccessMessage('Dry run queued.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to queue dry run.');
    } finally {
      setIsRunCreating(false);
    }
  };

  const handleOpenAddBillingSetting = () => {
    const defaultOrgId = orgId || organizations[0]?.id || '';
    setEditingBillingSettingOrgId(null);
    setBillingDefaultsForm({
      organisationId: defaultOrgId,
      currency: 'INR',
      taxRatePercent: '0',
      pricesTaxInclusive: false,
      taxLabel: 'Tax',
    });
    setIsBillingSettingsModalOpen(true);
  };

  const handleOpenEditBillingSetting = (setting: BillingOrgSettings) => {
    setEditingBillingSettingOrgId(setting.organisation_id);
    setBillingDefaultsForm({
      organisationId: setting.organisation_id,
      currency: setting.currency || 'INR',
      taxRatePercent: String(Number(setting.default_tax_rate_percent || 0)),
      pricesTaxInclusive: Boolean(setting.prices_tax_inclusive),
      taxLabel: setting.tax_label || 'Tax',
    });
    setIsBillingSettingsModalOpen(true);
  };

  const handleSaveBillingDefaults = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!billingDefaultsForm.organisationId) return;

    setIsSavingBillingDefaults(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updated = await billingOrgSettingsService.upsert(billingDefaultsForm.organisationId, {
        currency: billingDefaultsForm.currency.trim().toUpperCase() || 'INR',
        default_tax_rate_percent: Number(billingDefaultsForm.taxRatePercent || '0'),
        prices_tax_inclusive: billingDefaultsForm.pricesTaxInclusive,
        tax_label: billingDefaultsForm.taxLabel.trim() || 'Tax',
      });
      setBillingSettingsList((prev) => {
        const existingIdx = prev.findIndex((item) => item.organisation_id === updated.organisation_id);
        if (existingIdx === -1) {
          return [updated, ...prev];
        }
        const next = [...prev];
        next[existingIdx] = updated;
        return next;
      });
      setIsBillingSettingsModalOpen(false);
      setEditingBillingSettingOrgId(null);
      setSuccessMessage(editingBillingSettingOrgId ? 'Billing setting updated.' : 'Billing setting created.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save billing defaults.');
    } finally {
      setIsSavingBillingDefaults(false);
    }
  };

  const handleOpenCreateInvoiceModal = async () => {
    const { start, end } = getCurrentMonthDateRange();
    const targetOrgId = invoiceFilters.organisationId || orgId || '';
    const settings = targetOrgId ? await loadOrgBillingDefaults(targetOrgId) : null;
    const defaults = settings
      ? {
          currency: settings.currency || 'INR',
          taxRatePercent: String(Number(settings.default_tax_rate_percent || 0)),
          pricesTaxInclusive: Boolean(settings.prices_tax_inclusive),
          taxLabel: settings.tax_label || 'Tax',
        }
      : {
          currency: 'INR',
          taxRatePercent: '0',
          pricesTaxInclusive: false,
          taxLabel: 'Tax',
        };

    setInvoicePreview(null);
    setInvoiceForm({
      organisationId: targetOrgId,
      invoiceType: 'usage_settlement_end',
      periodStart: start,
      periodEnd: end,
      billingPlanId: '',
      status: 'draft',
      dueAt: '',
      ...defaults,
    });
    setIsCreateInvoiceModalOpen(true);
  };

  const handlePreviewInvoice = async () => {
    if (!invoiceForm.organisationId || !invoiceForm.periodStart || !invoiceForm.periodEnd) return;
    setIsInvoicePreviewLoading(true);
    setError(null);
    try {
      const computed = await billingInvoicesService.computeInvoiceDraftFromUsage({
        organisationId: invoiceForm.organisationId,
        periodStartIso: `${invoiceForm.periodStart}T00:00:00.000Z`,
        periodEndIso: `${invoiceForm.periodEnd}T23:59:59.999Z`,
        invoiceType: invoiceForm.invoiceType,
        billingPlanId: invoiceForm.billingPlanId || null,
        currencyOverride: invoiceForm.currency,
        taxRatePercentOverride: Number(invoiceForm.taxRatePercent || 0),
        pricesTaxInclusiveOverride: invoiceForm.pricesTaxInclusive,
        taxLabelOverride: invoiceForm.taxLabel,
      });
      const preview: InvoicePreview = {
        currency: computed.draftInput.currency,
        subtotal_amount: Number(computed.draftInput.subtotal_amount || 0),
        tax_amount: Number(computed.draftInput.tax_amount || 0),
        tax_rate_percent: Number(computed.draftInput.tax_rate_percent || 0),
        prices_tax_inclusive: Boolean(computed.draftInput.prices_tax_inclusive),
        tax_label: computed.draftInput.tax_label || 'Tax',
        total_amount: Number(computed.draftInput.subtotal_amount || 0) + Number(computed.draftInput.tax_amount || 0),
        line_items: computed.draftInput.line_items as InvoicePreviewLineItem[],
        note: (() => {
          const d = computed.debug;
          const usageNote = `Computed from usage data. Minutes: ${d.videoMinutesUsage.toFixed(2)}, Storage GB: ${d.storageGbUsage.toFixed(
            2
          )}, Accessible matches: ${d.lineup.accessibleMatchesCount}.`;
          const hasUsage = d.videoMinutesUsage > 0 || d.storageGbUsage > 0;
          const zeroAmounts = Number(computed.draftInput.subtotal_amount || 0) === 0;
          if (hasUsage && zeroAmounts) {
            const why: string[] = ['Amounts are zero because no pricing rule matched or rates are zero.'];
            if (d.matchFailureVideo) why.push(`Video: ${d.matchFailureVideo}`);
            if (d.matchFailureStorage) why.push(`Storage: ${d.matchFailureStorage}`);
            if (why.length === 1) why.push('Add pricing rules (Pricing Rules) for video minutes and/or storage with a positive rate.');
            return `${usageNote} ${why.join(' ')}`;
          }
          const parts: string[] = [usageNote];
          if (d.videoBreakdown && d.videoBreakdown.length > 0) {
            parts.push(
              '\n\nVideo calculation: Total raw ' +
                d.videoMinutesUsage.toFixed(2) +
                ' min, Included (free) ' +
                (d.videoMinutesIncluded ?? 0).toFixed(2) +
                ' min, Billable ' +
                d.videoMinutesBillable.toFixed(2) +
                ' min.'
            );
            d.videoBreakdown.forEach((v) => {
              parts.push(
                ` ${v.ruleName}: raw ${v.rawMinutes.toFixed(2)} min → billable ${v.billableMinutes.toFixed(2)} min × ${v.ratePerMinute} = ${computed.draftInput.currency ?? 'INR'} ${v.amount.toFixed(2)}.`
              );
            });
          }
          if (d.storageBreakdown) {
            const s = d.storageBreakdown;
            parts.push(
              `\n\nStorage: raw ${s.rawGb.toFixed(2)} GB, included ${s.includedGb.toFixed(2)} GB, billable ${s.billableGb.toFixed(2)} GB × ${s.ratePerGb} = ${computed.draftInput.currency ?? 'INR'} ${s.amount.toFixed(2)}.`
            );
          }
          return parts.join('');
        })(),
      };
      setInvoicePreview(preview);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to generate invoice preview.');
    } finally {
      setIsInvoicePreviewLoading(false);
    }
  };

  const handleCreateInvoiceFromPreview = async () => {
    if (!invoiceForm.organisationId || !invoiceForm.periodStart || !invoiceForm.periodEnd) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const computed = await billingInvoicesService.computeInvoiceDraftFromUsage({
        organisationId: invoiceForm.organisationId,
        periodStartIso: `${invoiceForm.periodStart}T00:00:00.000Z`,
        periodEndIso: `${invoiceForm.periodEnd}T23:59:59.999Z`,
        invoiceType: invoiceForm.invoiceType,
        billingPlanId: invoiceForm.billingPlanId || null,
        currencyOverride: invoiceForm.currency,
        taxRatePercentOverride: Number(invoiceForm.taxRatePercent || 0),
        pricesTaxInclusiveOverride: invoiceForm.pricesTaxInclusive,
        taxLabelOverride: invoiceForm.taxLabel,
      });
      await billingInvoicesService.createDraftInvoice({
        ...computed.draftInput,
        due_at: invoiceForm.dueAt ? `${invoiceForm.dueAt}T00:00:00.000Z` : null,
        status: invoiceForm.status,
      });

      setIsCreateInvoiceModalOpen(false);
      setInvoicePreview(null);
      setSuccessMessage('Draft invoice created.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create draft invoice.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExecuteQueuedRuns = async () => {
    setIsRunExecuting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const results = await billingInvoicesService.executeQueuedRuns(10);
      const completed = results.filter((x) => x.run.status === 'completed').length;
      const failed = results.filter((x) => x.run.status === 'failed').length;
      setSuccessMessage(`Run execution done. Completed: ${completed}, Failed: ${failed}.`);
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to execute queued runs.');
    } finally {
      setIsRunExecuting(false);
    }
  };

  const handleViewInvoice = async (invoice: BillingInvoice) => {
    setViewInvoice(invoice);
    setViewInvoiceLineItems([]);
    try {
      const items = await billingInvoicesService.getInvoiceLineItems(invoice.id);
      setViewInvoiceLineItems(items);
    } catch {
      setViewInvoiceLineItems([]);
    }
  };

  const handleConfirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    setError(null);
    try {
      await billingInvoicesService.deleteInvoice(invoiceToDelete.id);
      setInvoiceToDelete(null);
      setSuccessMessage('Invoice deleted.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete invoice.');
    }
  };

  const handleSaveInvoiceEdit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingInvoice) return;
    const form = event.currentTarget;
    const get = (name: string) => (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value;
    const dueAtRaw = get('due_at');
    const due_at = dueAtRaw ? `${dueAtRaw}T00:00:00.000Z` : null;
    const periodStart = get('period_start');
    const periodEnd = get('period_end');
    setIsSaving(true);
    setError(null);
    try {
      await billingInvoicesService.updateInvoice(editingInvoice.id, {
        organisation_id: get('organisation_id') || undefined,
        billing_plan_id: get('billing_plan_id') || null,
        invoice_type: (get('invoice_type') ?? editingInvoice.invoice_type) as BillingInvoice['invoice_type'],
        period_start: periodStart ? `${periodStart}T00:00:00.000Z` : undefined,
        period_end: periodEnd ? `${periodEnd}T23:59:59.999Z` : undefined,
        currency: get('currency') || undefined,
        tax_rate_percent: Number(get('tax_rate_percent')) || 0,
        tax_label: get('tax_label') || null,
        prices_tax_inclusive: (form.elements.namedItem('prices_tax_inclusive') as HTMLInputElement | null)?.checked ?? false,
        status: (get('status') ?? editingInvoice.status) as BillingInvoice['status'],
        due_at,
      });
      setEditingInvoice(null);
      setSuccessMessage('Invoice updated.');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update invoice.');
    } finally {
      setIsSaving(false);
    }
  };

  const getScopeLabel = (organizationId: string | null): string => {
    if (!organizationId) return 'Global';
    return orgNameById[organizationId] || organizationId;
  };

  const invoiceStatusLabel = (status: BillingInvoice['status']): string => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'issued': return 'Invoiced or billed';
      case 'paid': return 'Paid';
      default: return status.replace(/_/g, ' ');
    }
  };

  return (
    <div className="flex-1 p-6 overflow-y-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{sectionHeader.title}</h1>
        <p className="text-gray-600">{sectionHeader.description}</p>
      </div>

      {currentSection === 'plans' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Plans and entitlements</h2>
              <p className="text-xs text-gray-500 mt-1">List of billing plans. Create plans from the + Plan button.</p>
            </div>
            <button
              onClick={() => setIsAddPlanModalOpen(true)}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              + Plan
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200 bg-gray-50">
              <div className="col-span-3">Plan</div>
              <div className="col-span-2">Scope</div>
              <div className="col-span-2">Cadence</div>
              <div className="col-span-2">Base Fee</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Actions</div>
            </div>
            {plans.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-500">No plans created yet.</p>
            ) : (
              plans.map((plan) => (
                <div key={plan.id} className={`grid grid-cols-12 gap-2 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 ${selectedPlanId === plan.id ? 'bg-blue-50/40' : ''}`}>
                  <div className="col-span-3 font-medium text-gray-900">{plan.name}</div>
                  <div className="col-span-2 text-gray-700">{getScopeLabel(plan.organisation_id)}</div>
                  <div className="col-span-2 text-gray-700">{plan.cadence}</div>
                  <div className="col-span-2 text-gray-700">
                    {plan.currency} {Number(plan.base_subscription_fee || 0).toFixed(2)}
                  </div>
                  <div className="col-span-1">
                    <span className={`text-xs px-2 py-1 rounded ${plan.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-0.5">
                    <button
                      onClick={() => handleOpenEditPlan(plan)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
                      title="Edit plan"
                      aria-label="Edit plan"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleOpenEditEntitlements(plan)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
                      title="Edit entitlements"
                      aria-label="Edit entitlements"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setPlanToDelete(plan)}
                      className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded"
                      title="Delete plan"
                      aria-label="Delete plan"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      )}

      {currentSection === 'pricing-rules' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Pricing rules and conditions</h2>
              <p className="text-xs text-gray-500 mt-1">List of pricing rules. Create rules from the + Rule button.</p>
            </div>
            <button
              onClick={() => setIsAddRuleModalOpen(true)}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              + Rule
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200 bg-gray-50">
              <div className="col-span-3">Rule</div>
              <div className="col-span-2">Scope</div>
              <div className="col-span-2">Metric</div>
              <div className="col-span-2">Rate</div>
              <div className="col-span-1">Priority</div>
              <div className="col-span-2">Actions</div>
            </div>
            {rules.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-500">No pricing rules created yet.</p>
            ) : (
              rules.map((rule) => (
                <div key={rule.id} className={`grid grid-cols-12 gap-2 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 ${selectedRuleId === rule.id ? 'bg-blue-50/40' : ''}`}>
                  <div className="col-span-3 font-medium text-gray-900">{rule.name}</div>
                  <div className="col-span-2 text-gray-700">{getScopeLabel(rule.organisation_id)}</div>
                  <div className="col-span-2 text-gray-700">{rule.metric}</div>
                  <div className="col-span-2 text-gray-700">
                    {rule.rate_type} {parseFloat(Number(rule.rate_value || 0).toFixed(4)).toString()}
                  </div>
                  <div className="col-span-1 text-gray-700">{rule.priority}</div>
                  <div className="col-span-2 flex items-center justify-end gap-0.5">
                    <button
                      onClick={() => handleOpenEditRule(rule)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
                      title="Edit rule"
                      aria-label="Edit rule"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleOpenEditConditions(rule)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
                      title="Edit conditions"
                      aria-label="Edit conditions"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setRuleToDelete(rule)}
                      className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded"
                      title="Delete rule"
                      aria-label="Delete rule"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      )}

      {currentSection === 'invoices' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Invoices</h2>
              <p className="text-xs text-gray-500 mt-1">Filter invoices and create draft invoices by organization and billing period.</p>
            </div>
            <button
              onClick={handleOpenCreateInvoiceModal}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              + Invoice
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Organization</label>
              <select
                value={invoiceFilters.organisationId}
                onChange={(e) => setInvoiceFilters((prev) => ({ ...prev, organisationId: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="">All organizations</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Status</label>
              <select
                value={invoiceFilters.status}
                onChange={(e) => setInvoiceFilters((prev) => ({ ...prev, status: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="issued">Invoiced or billed</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">From date</label>
              <input
                type="date"
                value={invoiceFilters.fromDate}
                onChange={(e) => setInvoiceFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">To date</label>
              <input
                type="date"
                value={invoiceFilters.toDate}
                onChange={(e) => setInvoiceFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
          </div>

          {isLoading ? (
            <p className="text-sm text-gray-500">Loading invoices...</p>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-gray-500">No invoices available.</p>
          ) : (
            <div className="border border-gray-200 rounded-lg">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200 bg-gray-50">
                <div className="col-span-3">Invoice</div>
                <div className="col-span-2">Organization</div>
                <div className="col-span-2">Period</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2 text-right">Amount Due</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {invoices.map((invoice) => (
                <div key={invoice.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 items-center">
                  <div className="col-span-3">
                    <p className="font-medium text-gray-800">{invoice.invoice_number}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{invoice.invoice_type.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="col-span-2 text-gray-700">{orgNameById[invoice.organisation_id] || invoice.organisation_id}</div>
                  <div className="col-span-2 text-gray-700">
                    {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{invoiceStatusLabel(invoice.status)}</span>
                  </div>
                  <div className="col-span-2 text-right font-medium text-gray-900">
                    {invoice.currency} {Number(invoice.amount_due ?? invoice.total_amount ?? 0).toFixed(2)}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleViewInvoice(invoice)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
                      title="View invoice"
                      aria-label="View invoice"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingInvoice(invoice)}
                      className="p-1.5 text-gray-400 hover:text-amber-600 transition-colors rounded"
                      title="Edit invoice"
                      aria-label="Edit invoice"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setInvoiceToDelete(invoice)}
                      className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded"
                      title="Delete invoice"
                      aria-label="Delete invoice"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {currentSection === 'runs' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">Billing runs</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExecuteQueuedRuns}
                disabled={isRunExecuting}
                className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                {isRunExecuting ? 'Executing...' : 'Execute Queued'}
              </button>
              <button
                onClick={handleCreateDryRun}
                disabled={isRunCreating}
                className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
              >
                {isRunCreating ? 'Queuing...' : 'Queue Dry Run'}
              </button>
            </div>
          </div>
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading runs...</p>
          ) : runs.length === 0 ? (
            <p className="text-sm text-gray-500">No billing runs available.</p>
          ) : (
            <div className="space-y-2">
              {runs.map((run) => (
                <div key={run.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                  <div>
                    <p className="font-medium text-gray-800">{run.dry_run ? 'Dry run' : 'Settlement run'}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(run.period_start).toLocaleDateString()} - {new Date(run.period_end).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-700">{run.status}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {currentSection === 'billing-settings' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Billing settings</h2>
              <p className="text-xs text-gray-500 mt-1">Configure currency and tax defaults used for new invoices.</p>
            </div>
            <button
              onClick={handleOpenAddBillingSetting}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              + Billing Setting
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200 bg-gray-50">
              <div className="col-span-3">Organization</div>
              <div className="col-span-2">Currency</div>
              <div className="col-span-2">Default tax %</div>
              <div className="col-span-2">Tax label</div>
              <div className="col-span-2">Pricing mode</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {billingSettingsList.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-500">No billing settings configured yet.</p>
            ) : (
              billingSettingsList.map((setting) => (
                <div key={setting.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0">
                  <div className="col-span-3 text-gray-900 font-medium">{orgNameById[setting.organisation_id] || setting.organisation_id}</div>
                  <div className="col-span-2 text-gray-700">{setting.currency}</div>
                  <div className="col-span-2 text-gray-700">{Number(setting.default_tax_rate_percent || 0).toFixed(3)}</div>
                  <div className="col-span-2 text-gray-700">{setting.tax_label || 'Tax'}</div>
                  <div className="col-span-2 text-gray-700">{setting.prices_tax_inclusive ? 'Tax-inclusive' : 'Tax-exclusive'}</div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      onClick={() => handleOpenEditBillingSetting(setting)}
                      className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={isEditEntitlementsModalOpen}
        onClose={handleCloseEditEntitlements}
        title={`Edit entitlements – ${viewingPlanName || 'Plan'}`}
      >
        <div className="space-y-3">
          {editEntitlementsView === 'list' ? (
            <>
              {isViewingDetailsLoading ? (
                <p className="text-sm text-gray-500">Loading entitlements...</p>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Entitlements for this plan</span>
                    <button
                      type="button"
                      onClick={handleOpenAddEntitlementInModal}
                      className="text-xs px-2 py-1 rounded bg-gray-900 text-white hover:bg-black"
                    >
                      + Add entitlement
                    </button>
                  </div>
                  {viewingEntitlements.length === 0 ? (
                    <p className="text-sm text-gray-500">No entitlements. Add one above.</p>
                  ) : (
                    <div className="space-y-2">
                      {viewingEntitlements.map((entitlement) => (
                        <div
                          key={entitlement.id}
                          className="flex items-center justify-between border border-gray-200 rounded px-3 py-2 text-sm"
                        >
                          <div>
                            <p className="font-medium text-gray-900">
                              {entitlement.metric === 'video_minutes' ? 'Video minutes' : 'Storage'}
                            </p>
                            <p className="text-gray-700 mt-1">
                              Included: {Number(entitlement.included_units || 0).toFixed(4)} · Carryover:{' '}
                              {entitlement.carryover_enabled ? 'On' : 'Off'}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => handleOpenEditEntitlementInModal(entitlement)}
                              className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setEntitlementToDelete(entitlement)}
                              className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={handleCloseEditEntitlements}
                      className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <form onSubmit={handleSaveEntitlement} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700">Entitlement metric</label>
                  <select
                    value={entitlementForm.metric}
                    onChange={(e) =>
                      setEntitlementForm((prev) => {
                        const metric = e.target.value as 'video_minutes' | 'storage';
                        if (metric === 'storage') return { ...prev, metric, carryoverEnabled: false };
                        return { ...prev, metric };
                      })
                    }
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    disabled={!!editingEntitlementId}
                  >
                    <option value="video_minutes">Video minutes</option>
                    <option value="storage">Storage</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Included units per cycle</label>
                  <input
                    value={entitlementForm.includedUnits}
                    onChange={(e) => setEntitlementForm((prev) => ({ ...prev, includedUnits: e.target.value }))}
                    type="number"
                    step="0.0001"
                    min="0"
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded px-2 py-2">
                    <input
                      type="checkbox"
                      checked={entitlementForm.carryoverEnabled}
                      onChange={(e) => setEntitlementForm((prev) => ({ ...prev, carryoverEnabled: e.target.checked }))}
                      disabled={entitlementForm.metric === 'storage'}
                    />
                    Carryover enabled
                  </label>
                  {entitlementForm.metric === 'storage' && (
                    <p className="text-xs text-gray-500 mt-1">Carryover is not supported for storage.</p>
                  )}
                </div>
              </div>
              {entitlementForm.metric === 'video_minutes' && entitlementForm.carryoverEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700">Carryover cap type</label>
                    <select
                      value={entitlementForm.carryoverCapType}
                      onChange={(e) =>
                        setEntitlementForm((prev) => ({ ...prev, carryoverCapType: e.target.value as 'fixed' | 'percent_of_included' }))
                      }
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    >
                      <option value="fixed">Fixed cap</option>
                      <option value="percent_of_included">Percent of included</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Carryover cap value</label>
                    <input
                      value={entitlementForm.carryoverCapValue}
                      onChange={(e) => setEntitlementForm((prev) => ({ ...prev, carryoverCapValue: e.target.value }))}
                      type="number"
                      step="0.0001"
                      min="0"
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Carryover expiry (cycles)</label>
                    <input
                      value={entitlementForm.carryoverExpiryCycles}
                      onChange={(e) => {
                        const v = e.target.value;
                        const n = Math.min(1, Math.max(0, parseInt(v, 10) || 0));
                        setEntitlementForm((prev) => ({ ...prev, carryoverExpiryCycles: v === '' ? v : String(n) }));
                      }}
                      type="number"
                      min={0}
                      max={1}
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-0.5">Next billing cycle only (max 1).</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Carryover consumption order</label>
                    <select
                      value={entitlementForm.carryoverConsumptionOrder}
                      onChange={(e) =>
                        setEntitlementForm((prev) => ({
                          ...prev,
                          carryoverConsumptionOrder: e.target.value as 'carryover_first' | 'current_cycle_first',
                        }))
                      }
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    >
                      <option value="carryover_first">Use carryover first</option>
                      <option value="current_cycle_first">Use current cycle first</option>
                    </select>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditEntitlementsView('list')}
                  className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {editingEntitlementId ? 'Save entitlement' : 'Add entitlement'}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isEditConditionsModalOpen}
        onClose={handleCloseEditConditions}
        title={`Edit conditions – ${viewingRuleName || 'Rule'}`}
      >
        <div className="space-y-3">
          {editConditionsView === 'list' ? (
            <>
              {isViewingDetailsLoading ? (
                <p className="text-sm text-gray-500">Loading conditions...</p>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Conditions for this rule</span>
                    <button
                      type="button"
                      onClick={handleOpenAddConditionInModal}
                      className="text-xs px-2 py-1 rounded bg-gray-900 text-white hover:bg-black"
                    >
                      + Add condition
                    </button>
                  </div>
                  {viewingConditions.length === 0 ? (
                    <p className="text-sm text-gray-500">No conditions. Add one above.</p>
                  ) : (
                    <div className="space-y-2">
                      {viewingConditions.map((condition) => (
                        <div
                          key={condition.id}
                          className="flex items-center justify-between border border-gray-200 rounded px-3 py-2 text-sm"
                        >
                          <p className="font-medium text-gray-900">
                            {condition.field_key} {condition.operator} {condition.field_value}
                          </p>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => handleOpenEditCondition(condition)}
                              className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setConditionToDelete(condition)}
                              className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={handleCloseEditConditions}
                      className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <form
              onSubmit={editingConditionId ? handleUpdateCondition : handleCreateCondition}
              className="space-y-3"
            >
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-700">Condition (what the pricing engine uses)</label>
                  <select
                    value={conditionForm.fieldKey}
                    onChange={(e) => setConditionForm((prev) => ({ ...prev, fieldKey: e.target.value }))}
                    className="mt-1 w-full min-w-0 border border-gray-300 rounded px-2 py-2 text-sm"
                    required
                  >
                    {BILLING_CONDITION_FIELD_KEYS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                    {conditionForm.fieldKey &&
                      !BILLING_CONDITION_FIELD_KEYS.some((k) => k.value === conditionForm.fieldKey) && (
                        <option value={conditionForm.fieldKey}>Other: {conditionForm.fieldKey}</option>
                      )}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700">Operator</label>
                    <select
                      value={conditionForm.operator}
                      onChange={(e) =>
                        setConditionForm((prev) => ({ ...prev, operator: e.target.value as RuleOperator }))}
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    >
                      <option value="<">{'<'}</option>
                      <option value="<=">{'<='}</option>
                      <option value="=">{'='}</option>
                      <option value="!=">{'!='}</option>
                      <option value=">=">{'>='}</option>
                      <option value=">">{'>'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Field value</label>
                    <input
                      value={conditionForm.fieldValue}
                      onChange={(e) => setConditionForm((prev) => ({ ...prev, fieldValue: e.target.value }))}
                      placeholder="11"
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditConditionsView('list');
                    setEditingConditionId(null);
                  }}
                  className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {editingConditionId ? 'Save condition' : 'Add condition'}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isBillingSettingsModalOpen}
        onClose={() => {
          setIsBillingSettingsModalOpen(false);
          setEditingBillingSettingOrgId(null);
        }}
        title={editingBillingSettingOrgId ? 'Edit Billing Setting' : 'Create Billing Setting'}
      >
        <form onSubmit={handleSaveBillingDefaults} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-700">Organization</label>
              <select
                value={billingDefaultsForm.organisationId}
                onChange={(e) => setBillingDefaultsForm((prev) => ({ ...prev, organisationId: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                disabled={!!editingBillingSettingOrgId}
              >
                <option value="">Select organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Currency</label>
              <select
                value={billingDefaultsForm.currency}
                onChange={(e) => setBillingDefaultsForm((prev) => ({ ...prev, currency: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Default tax %</label>
              <input
                type="number"
                min="0"
                step="0.001"
                value={billingDefaultsForm.taxRatePercent}
                onChange={(e) => setBillingDefaultsForm((prev) => ({ ...prev, taxRatePercent: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Tax label</label>
              <input
                value={billingDefaultsForm.taxLabel}
                onChange={(e) => setBillingDefaultsForm((prev) => ({ ...prev, taxLabel: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                placeholder="GST"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded px-2 py-2 w-full">
                <input
                  type="checkbox"
                  checked={billingDefaultsForm.pricesTaxInclusive}
                  onChange={(e) => setBillingDefaultsForm((prev) => ({ ...prev, pricesTaxInclusive: e.target.checked }))}
                />
                Tax-inclusive prices
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setIsBillingSettingsModalOpen(false);
                setEditingBillingSettingOrgId(null);
              }}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSavingBillingDefaults || !billingDefaultsForm.organisationId}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
            >
              {isSavingBillingDefaults ? 'Saving...' : editingBillingSettingOrgId ? 'Save changes' : 'Create setting'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isAddPlanModalOpen}
        onClose={() => setIsAddPlanModalOpen(false)}
        title="Create Billing Plan"
      >
        <form onSubmit={handleCreatePlan} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Plan scope</label>
              <select
                value={planForm.organizationScope}
                onChange={(e) =>
                  setPlanForm((prev) => ({
                    ...prev,
                    organizationScope: e.target.value as 'current' | 'global' | 'specific',
                  }))
                }
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="current">Current org</option>
                <option value="global">Global default</option>
                <option value="specific">Specific org</option>
              </select>
            </div>
            {planForm.organizationScope === 'specific' && (
              <div>
                <label className="text-xs font-medium text-gray-700">Specific organization</label>
                <select
                  value={planForm.specificOrganizationId}
                  onChange={(e) => setPlanForm((prev) => ({ ...prev, specificOrganizationId: e.target.value }))}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="">Select organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-700">Plan name</label>
              <input
                value={planForm.name}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Example: Basic"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Billing cadence</label>
              <select
                value={planForm.cadence}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, cadence: e.target.value as BillingPlan['cadence'] }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Currency</label>
              <select
                value={planForm.currency}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, currency: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Base subscription fee</label>
              <input
                value={planForm.baseFee}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, baseFee: e.target.value }))}
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-0.5">{planForm.currency}</p>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-3 mt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">Entitlements (optional)</span>
              <button
                type="button"
                onClick={addPlanAddEntitlementDraft}
                className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                + Add entitlement
              </button>
            </div>
            {addPlanEntitlementDrafts.length === 0 ? (
              <p className="text-xs text-gray-500">Add one or more entitlements to create with the plan.</p>
            ) : (
              <div className="space-y-2">
                {addPlanEntitlementDrafts.map((draft, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-4 gap-2 p-2 border border-gray-200 rounded bg-gray-50"
                  >
                    <select
                      value={draft.metric}
                      onChange={(e) =>
                        addPlanUpdateEntitlementDraft(index, {
                          metric: e.target.value as 'video_minutes' | 'storage',
                          carryoverEnabled: e.target.value === 'storage' ? false : draft.carryoverEnabled,
                        })
                      }
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="video_minutes">Video minutes</option>
                      <option value="storage">Storage</option>
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={draft.includedUnits}
                      onChange={(e) => addPlanUpdateEntitlementDraft(index, { includedUnits: e.target.value })}
                      placeholder="Units"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                    <label className="flex items-center gap-1 text-xs col-span-1">
                      <input
                        type="checkbox"
                        checked={draft.carryoverEnabled}
                        disabled={draft.metric === 'storage'}
                        onChange={(e) => addPlanUpdateEntitlementDraft(index, { carryoverEnabled: e.target.checked })}
                      />
                      Carryover
                    </label>
                    <button
                      type="button"
                      onClick={() => addPlanRemoveEntitlementDraft(index)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setIsAddPlanModalOpen(false);
                setAddPlanEntitlementDrafts([]);
              }}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || (planForm.organizationScope === 'specific' && !planForm.specificOrganizationId)}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
            >
              Create plan
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isAddRuleModalOpen}
        onClose={() => setIsAddRuleModalOpen(false)}
        title="Create Pricing Rule"
      >
        <form onSubmit={handleCreateRule} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Rule scope</label>
              <select
                value={ruleForm.organizationScope}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    organizationScope: e.target.value as 'current' | 'global' | 'specific',
                  }))
                }
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="current">Current org</option>
                <option value="global">Global default</option>
                <option value="specific">Specific org</option>
              </select>
            </div>
            {ruleForm.organizationScope === 'specific' && (
              <div>
                <label className="text-xs font-medium text-gray-700">Specific organization</label>
                <select
                  value={ruleForm.specificOrganizationId}
                  onChange={(e) => setRuleForm((prev) => ({ ...prev, specificOrganizationId: e.target.value }))}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="">Select organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-700">Rule name</label>
              <input
                value={ruleForm.name}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Example: Lineup based rate"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Metric</label>
              <select
                value={ruleForm.metric}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    metric: e.target.value as 'video_minutes' | 'storage' | 'service_combo',
                  }))
                }
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="video_minutes">Video minutes</option>
                <option value="storage">Storage</option>
                <option value="service_combo">Service combo</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Priority</label>
              <input
                value={ruleForm.priority}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, priority: e.target.value }))}
                type="number"
                placeholder="100"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Rate type</label>
              <select
                value={ruleForm.rateType}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    rateType: e.target.value as 'per_minute' | 'per_gb' | 'flat' | 'slab_flat' | 'slab_progressive',
                  }))
                }
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="per_minute">Per minute</option>
                <option value="per_gb">Per GB</option>
                <option value="flat">Flat</option>
                <option value="slab_flat">Slab flat</option>
                <option value="slab_progressive">Slab progressive</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Rate value</label>
              <input
                value={ruleForm.rateValue}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, rateValue: e.target.value }))}
                type="number"
                step="0.0001"
                min="0"
                placeholder="0"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-0.5">Uses the organisation&apos;s billing currency (Billing settings).</p>
            </div>
          </div>

          {ruleForm.metric === 'service_combo' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-700">Combination match mode</label>
                <select
                  value={ruleForm.comboMode}
                  onChange={(e) => setRuleForm((prev) => ({ ...prev, comboMode: e.target.value as 'exact' | 'contains' }))}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="contains">Contains</option>
                  <option value="exact">Exact</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Combination rate behavior</label>
                <select
                  value={ruleForm.comboRateMode}
                  onChange={(e) => setRuleForm((prev) => ({ ...prev, comboRateMode: e.target.value as 'override' | 'additive' }))}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="additive">Additive</option>
                  <option value="override">Override</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Services (comma separated)</label>
                <input
                  value={ruleForm.comboServicesCsv}
                  onChange={(e) => setRuleForm((prev) => ({ ...prev, comboServicesCsv: e.target.value }))}
                  placeholder="match_stats, highlights"
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setIsAddRuleModalOpen(false)}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || (ruleForm.organizationScope === 'specific' && !ruleForm.specificOrganizationId)}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
            >
              Create rule
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditPlanModalOpen}
        onClose={() => {
          setIsEditPlanModalOpen(false);
          setEditingPlanId(null);
        }}
        title="Edit Billing Plan"
      >
        <form onSubmit={handleUpdatePlan} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Plan scope</label>
              <select
                value={planForm.organizationScope}
                onChange={(e) =>
                  setPlanForm((prev) => ({
                    ...prev,
                    organizationScope: e.target.value as 'current' | 'global' | 'specific',
                  }))
                }
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="current">Current org</option>
                <option value="global">Global default</option>
                <option value="specific">Specific org</option>
              </select>
            </div>
            {planForm.organizationScope === 'specific' && (
              <div>
                <label className="text-xs font-medium text-gray-700">Specific organization</label>
                <select
                  value={planForm.specificOrganizationId}
                  onChange={(e) => setPlanForm((prev) => ({ ...prev, specificOrganizationId: e.target.value }))}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="">Select organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-700">Plan name</label>
              <input
                value={planForm.name}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, name: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Billing cadence</label>
              <select
                value={planForm.cadence}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, cadence: e.target.value as BillingPlan['cadence'] }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Currency</label>
              <select
                value={planForm.currency}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, currency: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Base subscription fee</label>
              <input
                value={planForm.baseFee}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, baseFee: e.target.value }))}
                type="number"
                min="0"
                step="0.01"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-0.5">{planForm.currency}</p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setIsEditPlanModalOpen(false);
                setEditingPlanId(null);
              }}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || (planForm.organizationScope === 'specific' && !planForm.specificOrganizationId)}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
            >
              Save changes
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditRuleModalOpen}
        onClose={() => {
          setIsEditRuleModalOpen(false);
          setEditingRuleId(null);
        }}
        title="Edit Pricing Rule"
      >
        <form onSubmit={handleUpdateRule} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Rule scope</label>
              <select
                value={ruleForm.organizationScope}
                onChange={(e) =>
                  setRuleForm((prev) => ({
                    ...prev,
                    organizationScope: e.target.value as 'current' | 'global' | 'specific',
                  }))
                }
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="current">Current org</option>
                <option value="global">Global default</option>
                <option value="specific">Specific org</option>
              </select>
            </div>
            {ruleForm.organizationScope === 'specific' && (
              <div>
                <label className="text-xs font-medium text-gray-700">Specific organization</label>
                <select
                  value={ruleForm.specificOrganizationId}
                  onChange={(e) => setRuleForm((prev) => ({ ...prev, specificOrganizationId: e.target.value }))}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="">Select organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-700">Rule name</label>
              <input
                value={ruleForm.name}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, name: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Metric</label>
              <select
                value={ruleForm.metric}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, metric: e.target.value as 'video_minutes' | 'storage' | 'service_combo' }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="video_minutes">Video minutes</option>
                <option value="storage">Storage</option>
                <option value="service_combo">Service combo</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Priority</label>
              <input
                value={ruleForm.priority}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, priority: e.target.value }))}
                type="number"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Rate type</label>
              <select
                value={ruleForm.rateType}
                onChange={(e) =>
                  setRuleForm((prev) => ({ ...prev, rateType: e.target.value as 'per_minute' | 'per_gb' | 'flat' | 'slab_flat' | 'slab_progressive' }))
                }
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="per_minute">Per minute</option>
                <option value="per_gb">Per GB</option>
                <option value="flat">Flat</option>
                <option value="slab_flat">Slab flat</option>
                <option value="slab_progressive">Slab progressive</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Rate value</label>
              <input
                value={ruleForm.rateValue}
                onChange={(e) => setRuleForm((prev) => ({ ...prev, rateValue: e.target.value }))}
                type="number"
                step="0.0001"
                min="0"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-0.5">Uses the organisation&apos;s billing currency (Billing settings).</p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setIsEditRuleModalOpen(false);
                setEditingRuleId(null);
              }}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || (ruleForm.organizationScope === 'specific' && !ruleForm.specificOrganizationId)}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
            >
              Save changes
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isCreateInvoiceModalOpen}
        onClose={() => {
          setIsCreateInvoiceModalOpen(false);
          setInvoicePreview(null);
          setInvoiceOrgSearchQuery('');
          setInvoiceOrgDropdownOpen(false);
        }}
        title="Create Draft Invoice"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <label className="text-xs font-medium text-gray-700">Organization</label>
              <input
                type="text"
                value={invoiceOrgDropdownOpen ? invoiceOrgSearchQuery : (organizations.find((o) => o.id === invoiceForm.organisationId)?.name ?? 'Select organization')}
                onChange={(e) => {
                  setInvoiceOrgSearchQuery(e.target.value);
                  setInvoiceOrgDropdownOpen(true);
                }}
                onFocus={() => {
                  setInvoiceOrgDropdownOpen(true);
                  if (!invoiceOrgSearchQuery) setInvoiceOrgSearchQuery('');
                }}
                onBlur={() => setTimeout(() => setInvoiceOrgDropdownOpen(false), 150)}
                placeholder="Search or select organization"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
              {invoiceOrgDropdownOpen && (
                <ul
                  className="absolute z-10 mt-0.5 w-full max-h-48 overflow-auto border border-gray-300 rounded bg-white shadow-lg text-sm"
                  role="listbox"
                >
                  <li
                    role="option"
                    className="px-2 py-2 cursor-pointer hover:bg-gray-100 text-gray-500"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setInvoiceForm((prev) => ({ ...prev, organisationId: '', billingPlanId: '' }));
                      setInvoiceOrgSearchQuery('');
                      setInvoiceOrgDropdownOpen(false);
                      setInvoicePreview(null);
                    }}
                  >
                    Select organization
                  </li>
                  {organizations
                    .filter((org) => !invoiceOrgSearchQuery.trim() || org.name.toLowerCase().includes(invoiceOrgSearchQuery.trim().toLowerCase()))
                    .map((org) => (
                      <li
                        key={org.id}
                        role="option"
                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                        onMouseDown={async (e) => {
                          e.preventDefault();
                          const settings = await loadOrgBillingDefaults(org.id);
                          const defaults = settings
                            ? {
                                currency: settings.currency || 'INR',
                                taxRatePercent: String(Number(settings.default_tax_rate_percent || 0)),
                                pricesTaxInclusive: Boolean(settings.prices_tax_inclusive),
                                taxLabel: settings.tax_label || 'Tax',
                              }
                            : {
                                currency: 'INR',
                                taxRatePercent: '0',
                                pricesTaxInclusive: false,
                                taxLabel: 'Tax',
                              };
                          setInvoiceForm((prev) => ({ ...prev, organisationId: org.id, billingPlanId: '', ...defaults }));
                          setInvoiceOrgSearchQuery('');
                          setInvoiceOrgDropdownOpen(false);
                          setInvoicePreview(null);
                        }}
                      >
                        {org.name}
                      </li>
                    ))}
                </ul>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Invoice type</label>
              <select
                value={invoiceForm.invoiceType}
                onChange={(e) => {
                  setInvoiceForm((prev) => ({ ...prev, invoiceType: e.target.value as BillingInvoice['invoice_type'] }));
                  setInvoicePreview(null);
                }}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="usage_settlement_end">Usage settlement (end cycle)</option>
                <option value="subscription_start">Subscription (start cycle)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Period start</label>
              <input
                type="date"
                value={invoiceForm.periodStart}
                onChange={(e) => {
                  setInvoiceForm((prev) => ({ ...prev, periodStart: e.target.value }));
                  setInvoicePreview(null);
                }}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Period end</label>
              <input
                type="date"
                value={invoiceForm.periodEnd}
                onChange={(e) => {
                  setInvoiceForm((prev) => ({ ...prev, periodEnd: e.target.value }));
                  setInvoicePreview(null);
                }}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-700">Billing plan (optional override)</label>
              <select
                value={invoiceForm.billingPlanId}
                onChange={(e) => {
                  const nextPlanId = e.target.value;
                  const nextPlan = plans.find((plan) => plan.id === nextPlanId);
                  setInvoiceForm((prev) => ({
                    ...prev,
                    billingPlanId: nextPlanId,
                    currency: nextPlan?.currency || prev.currency,
                  }));
                  setInvoicePreview(null);
                }}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="">Auto-select plan</option>
                {plans
                  .filter((plan) => !invoiceForm.organisationId || !plan.organisation_id || plan.organisation_id === invoiceForm.organisationId)
                  .map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({plan.organisation_id ? getScopeLabel(plan.organisation_id) : 'Global'})
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Currency</label>
              <input
                value={invoiceForm.currency}
                onChange={(e) => {
                  setInvoiceForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }));
                  setInvoicePreview(null);
                }}
                placeholder="INR"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Tax rate %</label>
              <input
                type="number"
                min="0"
                step="0.001"
                value={invoiceForm.taxRatePercent}
                onChange={(e) => {
                  setInvoiceForm((prev) => ({ ...prev, taxRatePercent: e.target.value }));
                  setInvoicePreview(null);
                }}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Tax label</label>
              <input
                value={invoiceForm.taxLabel}
                onChange={(e) => {
                  setInvoiceForm((prev) => ({ ...prev, taxLabel: e.target.value }));
                  setInvoicePreview(null);
                }}
                placeholder="GST"
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded px-2 py-2 mt-5 md:mt-0">
                <input
                  type="checkbox"
                  checked={invoiceForm.pricesTaxInclusive}
                  onChange={(e) => {
                    setInvoiceForm((prev) => ({ ...prev, pricesTaxInclusive: e.target.checked }));
                    setInvoicePreview(null);
                  }}
                />
                Prices are tax-inclusive
              </label>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Status</label>
              <select
                value={invoiceForm.status}
                onChange={(e) => {
                  setInvoiceForm((prev) => ({ ...prev, status: e.target.value as BillingInvoice['status'] }));
                }}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="draft">Draft</option>
                <option value="issued">Invoiced or billed</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Due date</label>
              <input
                type="date"
                value={invoiceForm.dueAt}
                onChange={(e) => setInvoiceForm((prev) => ({ ...prev, dueAt: e.target.value }))}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
              />
            </div>
          </div>

          {invoicePreview && (
            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <p className="text-xs text-gray-500 whitespace-pre-line">{invoicePreview.note}</p>
              <div className="space-y-1">
                {invoicePreview.line_items.map((line, idx) => (
                  <div key={`${line.description}-${idx}`} className="flex items-center justify-between text-sm">
                    <p className="text-gray-700">{line.description}</p>
                    <p className="font-medium text-gray-900">
                      {invoicePreview.currency} {Number(line.amount || 0).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-gray-200 space-y-1 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium text-gray-900">
                    {invoicePreview.currency} {invoicePreview.subtotal_amount.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">
                    {invoicePreview.tax_label} ({Number(invoicePreview.tax_rate_percent) % 1 === 0 ? String(Math.round(invoicePreview.tax_rate_percent)) : String(Number(invoicePreview.tax_rate_percent))}%)
                  </span>
                  <span className="font-medium text-gray-900">
                    {invoicePreview.currency} {invoicePreview.tax_amount.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-semibold">Total</span>
                  <span className="font-semibold text-gray-900">
                    {invoicePreview.currency} {invoicePreview.total_amount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setIsCreateInvoiceModalOpen(false);
                setInvoicePreview(null);
              }}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePreviewInvoice}
              disabled={
                isInvoicePreviewLoading || !invoiceForm.organisationId || !invoiceForm.periodStart || !invoiceForm.periodEnd || isSaving
              }
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm disabled:opacity-50"
            >
              {isInvoicePreviewLoading ? 'Calculating...' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={handleCreateInvoiceFromPreview}
              disabled={!invoiceForm.organisationId || !invoiceForm.periodStart || !invoiceForm.periodEnd || isSaving}
              className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
            >
              {isSaving ? 'Creating...' : 'Create draft'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!entitlementToDelete}
        onClose={() => setEntitlementToDelete(null)}
        title="Remove entitlement"
      >
        <div className="space-y-3">
          {entitlementToDelete && (
            <p className="text-sm text-gray-700">
              Remove this entitlement ({entitlementToDelete.metric === 'video_minutes' ? 'Video minutes' : 'Storage'} –{' '}
              {Number(entitlementToDelete.included_units ?? 0).toFixed(4)} units)? This cannot be undone.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEntitlementToDelete(null)}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDeleteEntitlement}
              className="px-3 py-2 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700"
            >
              Remove
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!conditionToDelete}
        onClose={() => setConditionToDelete(null)}
        title="Remove condition"
      >
        <div className="space-y-3">
          {conditionToDelete && (
            <p className="text-sm text-gray-700">
              Remove this condition ({conditionToDelete.field_key} {conditionToDelete.operator} {conditionToDelete.field_value})? This cannot be undone.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConditionToDelete(null)}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDeleteCondition}
              className="px-3 py-2 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700"
            >
              Remove
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!planToDelete}
        onClose={() => setPlanToDelete(null)}
        title="Delete billing plan"
      >
        <div className="space-y-3">
          {planToDelete && (
            <p className="text-sm text-gray-700">
              Delete plan &quot;{planToDelete.name}&quot;? All entitlements for this plan will be removed. This cannot be undone.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPlanToDelete(null)}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeletePlan}
              disabled={isSaving}
              className="px-3 py-2 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {isSaving ? 'Deleting...' : 'Delete plan'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!ruleToDelete}
        onClose={() => setRuleToDelete(null)}
        title="Delete pricing rule"
      >
        <div className="space-y-3">
          {ruleToDelete && (
            <p className="text-sm text-gray-700">
              Delete rule &quot;{ruleToDelete.name}&quot;? All conditions for this rule will be removed. This cannot be undone.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setRuleToDelete(null)}
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteRule}
              disabled={isSaving}
              className="px-3 py-2 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {isSaving ? 'Deleting...' : 'Delete rule'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!viewInvoice}
        onClose={() => { setViewInvoice(null); setViewInvoiceLineItems([]); }}
        title={viewInvoice ? `Invoice ${viewInvoice.invoice_number}` : 'Invoice'}
      >
        {viewInvoice && (() => {
          const taxInclusive = Boolean(viewInvoice.prices_tax_inclusive);
          const taxRate = Number(viewInvoice.tax_rate_percent || 0);
          const lineItemsTotal = viewInvoiceLineItems.reduce((sum, i) => sum + Number(i.amount || 0), 0);
          const displayTotal = taxInclusive && lineItemsTotal > 0
            ? Math.round(lineItemsTotal * 100) / 100
            : Number(viewInvoice.amount_due ?? viewInvoice.total_amount ?? 0);
          const displaySubtotal = taxInclusive && taxRate > 0
            ? Math.round((displayTotal / (1 + taxRate / 100)) * 100) / 100
            : Number(viewInvoice.subtotal_amount || 0);
          const displayTax = taxInclusive && taxRate > 0
            ? Math.round((displayTotal - displaySubtotal) * 100) / 100
            : Number(viewInvoice.tax_amount || 0);
          return (
          <div className="space-y-4">
            {taxInclusive && (
              <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                Prices are tax-inclusive. The amount due is the total the client pays; tax is shown as a breakdown of that total.
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-600">Organization</div>
              <div>{orgNameById[viewInvoice.organisation_id] || viewInvoice.organisation_id}</div>
              <div className="text-gray-600">Period</div>
              <div>{new Date(viewInvoice.period_start).toLocaleDateString()} – {new Date(viewInvoice.period_end).toLocaleDateString()}</div>
              <div className="text-gray-600">Type</div>
              <div>{viewInvoice.invoice_type.replace(/_/g, ' ')}</div>
              <div className="text-gray-600">Status</div>
              <div>{invoiceStatusLabel(viewInvoice.status)}</div>
              <div className="text-gray-600">Subtotal (pre-tax)</div>
              <div>{viewInvoice.currency} {displaySubtotal.toFixed(2)}</div>
              <div className="text-gray-600">Tax ({viewInvoice.tax_rate_percent}%)</div>
              <div>{viewInvoice.currency} {displayTax.toFixed(2)}</div>
              <div className="text-gray-600 font-medium">Amount due (total to pay)</div>
              <div className="font-medium">{viewInvoice.currency} {displayTotal.toFixed(2)}</div>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-700 mb-2">Line items</p>
              {viewInvoiceLineItems.length === 0 ? (
                <p className="text-sm text-gray-500">No line items.</p>
              ) : (
                <div className="border border-gray-200 rounded divide-y divide-gray-100">
                  {viewInvoiceLineItems.map((item) => (
                    <div key={item.id} className="flex justify-between px-3 py-2 text-sm">
                      <span className="text-gray-800">{item.description}</span>
                      <span className="font-medium">{viewInvoice.currency} {Number(item.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setViewInvoice(null); setViewInvoiceLineItems([]); }}
                className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
              >
                Close
              </button>
            </div>
          </div>
          );
        })()}
      </Modal>

      <Modal
        isOpen={!!editingInvoice}
        onClose={() => setEditingInvoice(null)}
        title={editingInvoice ? `Edit invoice ${editingInvoice.invoice_number}` : 'Edit invoice'}
      >
        {editingInvoice && (
          <form onSubmit={handleSaveInvoiceEdit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700">Organization</label>
                <select
                  name="organisation_id"
                  defaultValue={editingInvoice.organisation_id}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Invoice type</label>
                <select
                  name="invoice_type"
                  defaultValue={editingInvoice.invoice_type}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="usage_settlement_end">Usage settlement (end cycle)</option>
                  <option value="subscription_start">Subscription (start cycle)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Period start</label>
                <input
                  name="period_start"
                  type="date"
                  defaultValue={editingInvoice.period_start ? editingInvoice.period_start.slice(0, 10) : ''}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Period end</label>
                <input
                  name="period_end"
                  type="date"
                  defaultValue={editingInvoice.period_end ? editingInvoice.period_end.slice(0, 10) : ''}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-gray-700">Billing plan (optional override)</label>
                <select
                  name="billing_plan_id"
                  defaultValue={editingInvoice.billing_plan_id || ''}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="">Auto-select plan</option>
                  {plans
                    .filter((p) => !editingInvoice.organisation_id || p.organisation_id === editingInvoice.organisation_id)
                    .map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} ({plan.organisation_id ? getScopeLabel(plan.organisation_id) : 'Global'})
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Currency</label>
                <input
                  name="currency"
                  type="text"
                  defaultValue={editingInvoice.currency}
                  placeholder="INR"
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Tax rate %</label>
                <input
                  name="tax_rate_percent"
                  type="number"
                  min="0"
                  step="0.001"
                  defaultValue={editingInvoice.tax_rate_percent ?? 0}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Tax label</label>
                <input
                  name="tax_label"
                  type="text"
                  defaultValue={editingInvoice.tax_label ?? 'Tax'}
                  placeholder="GST"
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded px-2 py-2 mt-5 md:mt-0">
                  <input
                    type="checkbox"
                    name="prices_tax_inclusive"
                    defaultChecked={Boolean(editingInvoice.prices_tax_inclusive)}
                  />
                  Prices are tax-inclusive
                </label>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Status</label>
                <select
                  name="status"
                  defaultValue={
                    editingInvoice.status === 'paid' ? 'paid' : editingInvoice.status === 'draft' ? 'draft' : 'issued'
                  }
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="issued">Invoiced or billed</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Due date</label>
                <input
                  name="due_at"
                  type="date"
                  defaultValue={editingInvoice.due_at ? editingInvoice.due_at.slice(0, 10) : ''}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingInvoice(null)}
                className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-3 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={!!invoiceToDelete}
        onClose={() => setInvoiceToDelete(null)}
        title="Delete invoice"
      >
        {invoiceToDelete && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Delete invoice {invoiceToDelete.invoice_number}? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setInvoiceToDelete(null)}
                className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteInvoice}
                className="px-3 py-2 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Toast
        isVisible={toast.isVisible}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, isVisible: false, message: '' }))}
        duration={5000}
      />
    </div>
  );
};

export default BillingPage;
